import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  type DataTableColumn,
  Spinner,
  type BadgeProps,
} from '@/components/ui'

import { documentStatusBadgeVariant, documentStatusLabelKey } from '../discovery'
import { useDeleteDocument, useKnowledgeDocuments } from '../queries'
import { asNumber, type KnowledgeDocument } from '../types'

type BadgeVariant = NonNullable<BadgeProps['variant']>

/** `internal` = uso interno (orienta o agente sem ser repetido); qualquer outro valor = visivel ao cliente. */
function visibilityLabelKey(
  visibility: string,
): 'knowledge.documents.visibility.internal' | 'knowledge.documents.visibility.customer' {
  return visibility === 'internal'
    ? 'knowledge.documents.visibility.internal'
    : 'knowledge.documents.visibility.customer'
}

function visibilityBadgeVariant(visibility: string): BadgeVariant {
  return visibility === 'internal' ? 'neutral' : 'success'
}

interface DocumentActionsProps {
  readonly document: KnowledgeDocument
  readonly deleteMutation: ReturnType<typeof useDeleteDocument>
}

/**
 * Ações de uma linha. A exclusao pede confirmacao inline (troca o botao de lixeira pela pergunta +
 * [Sim, excluir]/[Cancelar]) — deixando claro que remove o documento e o que o agente aprendeu dele. Estado
 * proprio por linha (por isso e um componente, e nao apenas uma funcao de celula).
 */
function DocumentActions({ document, deleteMutation }: DocumentActionsProps) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const [failed, setFailed] = useState(false)

  const isDeleting = deleteMutation.isPending && deleteMutation.variables === document.id

  const remove = () => {
    setFailed(false)
    deleteMutation.mutate(document.id, {
      onSuccess: () => {
        setConfirming(false)
      },
      // Sem onError o operador nao saberia que a exclusao falhou (ficaria achando que funcionou).
      onError: () => {
        setFailed(true)
      },
    })
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => {
          setConfirming(true)
        }}
        aria-label={t('knowledge.documents.delete.action', { name: document.name })}
        className="rounded text-foreground-muted transition-colors hover:text-status-danger focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
      >
        <Trash2 aria-hidden="true" className="h-4 w-4" />
      </button>
    )
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <span className="text-xs text-foreground-muted">
        {t('knowledge.documents.delete.confirmQuestion')}
      </span>
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="danger" loading={isDeleting} onClick={remove}>
          {t('knowledge.documents.delete.confirm')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={isDeleting}
          onClick={() => {
            setConfirming(false)
          }}
        >
          {t('knowledge.documents.delete.cancel')}
        </Button>
      </div>
      {failed ? (
        <span className="text-xs text-status-danger">{t('knowledge.documents.delete.error')}</span>
      ) : null}
    </div>
  )
}

/** Tabela das paginas aprendidas + contagem por status (docs/design/09 secao 4). */
export function KnowledgeDocuments({ tenantId }: { readonly tenantId: string }) {
  const { t, i18n } = useTranslation()
  const query = useKnowledgeDocuments(tenantId)
  const deleteMutation = useDeleteDocument(tenantId)

  const formatDate = (value: string | null): string =>
    value === null
      ? '—'
      : new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(value),
        )

  const columns: readonly DataTableColumn<KnowledgeDocument>[] = [
    {
      key: 'name',
      header: t('knowledge.documents.columns.name'),
      cellClassName: 'max-w-[20rem] truncate text-foreground-primary',
      cell: (document) => document.name,
    },
    {
      key: 'visibility',
      header: t('knowledge.documents.columns.visibility'),
      cell: (document) => (
        <Badge variant={visibilityBadgeVariant(document.visibility)}>
          {t(visibilityLabelKey(document.visibility))}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: t('knowledge.documents.columns.status'),
      cell: (document) => (
        <Badge variant={documentStatusBadgeVariant(document.status)}>
          {t(documentStatusLabelKey(document.status))}
        </Badge>
      ),
    },
    {
      key: 'learnedAt',
      header: t('knowledge.documents.columns.learnedAt'),
      cellClassName: 'text-foreground-muted',
      cell: (document) => formatDate(document.indexedAt),
    },
    {
      key: 'actions',
      header: t('knowledge.documents.columns.actions'),
      headerSrOnly: true,
      align: 'right',
      cell: (document) => <DocumentActions document={document} deleteMutation={deleteMutation} />,
    },
  ]

  let body
  if (query.isPending) {
    body = <Spinner label={t('loading')} />
  } else if (query.isError) {
    body = <Alert variant="danger" title={t('knowledge.documents.loadError')} />
  } else if (query.data.documents.length === 0) {
    body = <p className="text-sm text-foreground-muted">{t('knowledge.documents.empty')}</p>
  } else {
    body = (
      <DataTable
        columns={columns}
        rows={query.data.documents}
        rowKey={(document) => document.id}
        caption={t('knowledge.documents.title')}
        minWidthClassName="min-w-[36rem]"
      />
    )
  }

  const summary =
    query.isSuccess && query.data.documents.length > 0
      ? t('knowledge.documents.summary', {
          ready: asNumber(query.data.ready),
          total: asNumber(query.data.total),
        })
      : null

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3">
        <CardTitle>{t('knowledge.documents.title')}</CardTitle>
        {summary !== null ? <Badge variant="neutral">{summary}</Badge> : null}
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  )
}
